import { useRef, type PointerEvent as ReactPointerEvent, type MouseEvent as ReactMouseEvent } from 'react';

/** px of travel before a wobbling click becomes a drag. */
const SLOP = 4;

/**
 * Mouse and pen only: taking a touch would take the page's vertical scroll with it. `scroll-behavior`
 * goes off with the snapping because `.carousel` sets `smooth`, and each `scrollLeft` write would
 * restart an ease (measured: a 700px gesture moved the track 47px). `onDragEnd` runs BEFORE snapping
 * returns, or the browser settles the track somewhere other than where it ended.
 */
export function useDragScroll<T extends HTMLElement>(options: { onDragEnd?: () => void } = {}) {
	const drag = useRef<{ id: number; x: number; left: number; moved: boolean } | null>(null);
	const swallowClick = useRef(false);

	const onPointerDown = (event: ReactPointerEvent<T>) => {
		// Left button only: a right-click opens a menu and a middle-click is the browser's own scroll.
		if (event.pointerType === 'touch' || event.button !== 0) return;
		const el = event.currentTarget;
		drag.current = { id: event.pointerId, x: event.clientX, left: el.scrollLeft, moved: false };
		swallowClick.current = false;
		el.setPointerCapture(event.pointerId);
		el.style.cursor = 'grabbing';
	};

	const onPointerMove = (event: ReactPointerEvent<T>) => {
		const state = drag.current;
		if (!state || state.id !== event.pointerId) return;
		const dx = event.clientX - state.x;
		if (!state.moved) {
			if (Math.abs(dx) < SLOP) return;
			state.moved = true;
			// Only once it is a drag, so a plain click leaves the track's settings alone.
			event.currentTarget.style.scrollSnapType = 'none';
			event.currentTarget.style.scrollBehavior = 'auto';
		}
		event.currentTarget.scrollLeft = state.left - dx;
	};

	const onPointerUp = (event: ReactPointerEvent<T>) => {
		const state = drag.current;
		if (!state || state.id !== event.pointerId) return;
		const el = event.currentTarget;
		drag.current = null;
		swallowClick.current = state.moved;
		el.style.cursor = '';
		if (el.hasPointerCapture(event.pointerId)) el.releasePointerCapture(event.pointerId);
		// Smooth scrolling first, so the caller's landing glides the way the rail's does.
		el.style.scrollBehavior = '';
		// Before snapping returns, see above.
		if (state.moved) options.onDragEnd?.();
		el.style.scrollSnapType = '';
	};

	const onClickCapture = (event: ReactMouseEvent<T>) => {
		if (!swallowClick.current) return;
		swallowClick.current = false;
		event.preventDefault();
		event.stopPropagation();
	};

	return {
		style: { cursor: 'grab' },
		onPointerDown,
		onPointerMove,
		onPointerUp,
		onPointerCancel: onPointerUp,
		onClickCapture,
		// Dragging an image starts the browser's own drag-and-drop, which cuts the pointer stream.
		onDragStart: (event: ReactMouseEvent<T>) => event.preventDefault(),
	};
}
